import React, {Component} from 'react';
import { datadogRum } from "@datadog/browser-rum";
import {connect} from 'react-redux';
import { Loader } from "mesh-component-library";
import {refreshEpcsStatus, requestEpcsStatus} from '../../action';
import _isEmpty from 'lodash/isEmpty';
import _isNil from "lodash/isNil";
import CreditCardInfo from '../creditcard-info';
import { EPCS_SCRIPT_ID } from '../../app-consts/appConstants';
import { getServiceInfo } from "../../selector";

class EpcsIframe extends Component {

  constructor(props) {
    super(props);

    this.state = {
      epcsScriptLoaded: !!props?.epcsScriptLoaded || false,
      iFrameLoadSuccessful: false,
      paymentIframeLoadFailure: false,
      epcsStatusRequest: {},
    };
    // Deduplication guard: prevents requestEpcsStatus from being dispatched more than once
    // per submit attempt. When the payment page is closed and reopened after validation
    // errors, the EPCS script is reloaded and adds a new window.message listener alongside
    // the old one (removing a <script> tag from the DOM does not unload the script or its
    // listeners). This causes submitCompleteCallback to fire from both the stale and new
    // listeners, producing two status API calls and potential duplicate charges.
    // Reset to false on each fresh controller creation and after each reInit.
    this._hasRequestedStatus = false;
  }

  componentDidMount() {
    this.loadEpcsScript()
    this.props.onRef(this);
  }

  componentWillUnmount() {
    this.removeEpcsScript();
    this.props.onRef(undefined);
  }

  onLoadHandler = (e) => {
    // Reset the deduplication guard each time a fresh controller is created,
    // so the new submit attempt is allowed through exactly once.
    this._hasRequestedStatus = false;
    let epcsFrame = new Aic.Epcs.Controller({
      applicationID: this.props.epcsInitialize.iframeInstanceId,
      targetOrigin: this.props.epcsInitialize.targetOrigin,
      proxyUrl: this.props.epcsInitialize.proxyEndpoint,
      loggingServiceUrl: this.props.epcsInitialize.loggingServiceUrl.replace("&amp;", "&"),
      submitTimeout: 500000,
      submitCompleteCallback: async (event) => {
        if (event.success && !this.props.hasValidCard) {
          // Guard against duplicate status calls that can occur when the EPCS script
          // has been reloaded (close/reopen page) and old message listeners from a
          // previous script execution are still active, causing this callback to fire
          // more than once for a single submit attempt.
          if (this._hasRequestedStatus) {
            return;
          }
          this._hasRequestedStatus = true;
          this.props.refreshEpcsStatus();
          this.props.requestEpcsStatus(this.state.epcsStatusRequest);
          datadogRum.addAction("Payment submitted from EPCS iframe", {
              epcsStatusRequest: this.state.epcsStatusRequest
          });
        } else {
          if (event.hasValidationErrors) {
            this.props.onValidationError();
            if (typeof this.props.setIsSubmitting === 'function') {
              this.props.setIsSubmitting(false);
            }
            datadogRum.addError(new Error('EPCS Validation error'), {
              epcsStatusRequest: this.state.epcsStatusRequest
            });
            // Bug: D-207668==>After a validation error the EPCS controller is left in a broken
            // state where subsequent submit() calls never fire the callback.
            // Re-initialise the controller against the same iframe element so
            // the next submit attempt works without forcing the user to
            // re-enter their card details.
            this.reInitEpcsController();
          }
        }
      },
      registerFrameCompleteCallback: this.registerFrameCompleteCallback
    });
    epcsFrame.registerFrame(e.target);
    this.setState({ epcsFrame });
  };

  registerFrameCompleteCallback = (ev) => {
    if (!ev.isVerified) {
      this.setState({ paymentIframeLoadFailure: true });
      this.removeEpcsScript()
    } else {
      this.setState({
        paymentIframeLoadFailure: false,
        iFrameLoadSuccessful: true,
      });
      // Notify the parent (Payment screen) that the iframe is verified and ready
      // for submission. This re-enables the Confirm button after a validation
      // error triggers reInitEpcsController(), since that flow re-registers the
      // frame and eventually lands back here once verification succeeds again.
      this.notifyIframeReadyChange(true);
    }
  };

  // Bubbles the iframe's ready/not-ready state up to the parent
  // Payment screen via the onIframeReadyChange prop, so the Confirm button can
  // be disabled while the EPCS controller is being re-initialized after a
  // validation error, and re-enabled once the frame reports it is ready again.
  notifyIframeReadyChange = (isReady) => {
    if (typeof this.props.onIframeReadyChange === 'function') {
      this.props.onIframeReadyChange(isReady);
    }
  };

  reloadIframeHandler = () => {
    this.removeEpcsScript();
    this.loadEpcsScript();
    this.setState({
      paymentIframeLoadFailure: false,
    });
  };

  reInitEpcsController = () => {
    const { iframeInstanceId } = this.props.epcsInitialize;
    datadogRum.addAction("Re-initializing EPCS controller");
    if (this.state.epcsFrame && typeof this.state.epcsFrame.unregisterFrame === 'function') {
      this.state.epcsFrame.unregisterFrame(iframeInstanceId);
    }
    const iframeEl = document.getElementById(iframeInstanceId);
    if (iframeEl) {
      this.onLoadHandler({ target: iframeEl });
    }
  };

  removeEpcsScript = () => {
    const epcsScriptElement = document.getElementById(EPCS_SCRIPT_ID);
    if (epcsScriptElement !== null) {
      document.getElementsByTagName("head")[0].removeChild(epcsScriptElement);
    }
    if (
      !!this.props.epcsInitialize.iframeInstanceId &&
      !_isNil(this.state.epcsFrame) &&
      typeof this.state.epcsFrame.unregisterFrame === 'function'
    ) {
      this.state.epcsFrame.unregisterFrame(
        this.props.epcsInitialize.iframeInstanceId
      );
    }
  };

  loadEpcsScript = () => {
    if (!!this.props.epcsInitialize.jsUrl) {
      let epcsScriptTag = this.getLoadedEpcsScript();
      const epcsScriptElement = document.getElementById(EPCS_SCRIPT_ID);
      if (_isNil(epcsScriptTag) && _isNil(epcsScriptElement)) {
        let tag = document.createElement("script");
        tag.id = EPCS_SCRIPT_ID;
        tag.async = false;
        tag.src = this.props.epcsInitialize.jsUrl;
        tag.onload = () => {
          this.setState({
            epcsScriptLoaded: true
          })
        }
        document.getElementsByTagName("head")[0].appendChild(tag);
      }
    }
  };

  getLoadedEpcsScript = () => {
    let epcsScriptTag = null;
    let scripts = document.getElementsByTagName("script");
    Array.prototype.slice.call(scripts).forEach((scriptTag) => {
      if (scriptTag.src.toLowerCase() ===
        this.props.epcsInitialize.jsUrl.toLowerCase()) {
        epcsScriptTag = scriptTag;
        return;
      }
    });
    return epcsScriptTag;
  };

  handleEditCCSubmit = (zipcode, serviceType, amount, callId, cvv, previousTransactionAmount) => {
    let epcsStatusRequest = {
      "epcsIFrameInstanceId": this.props.epcsInitialize.iframeInstanceId,
      "billingZip": zipcode,
      "serviceType" : serviceType,
      "amount": amount,
      "previousAmount": previousTransactionAmount,
      "callId": callId,
      "cvv": cvv
    };

    this.setState({ epcsStatusRequest }, () => {
      this.state.epcsFrame.submit();
    });
  }

  showEpcsIframe = () => {
    if (_isEmpty(this.props.epcsStatus.response)) {
      return (
        <div>
          {this.state.epcsScriptLoaded ?
            <iframe id={this.props.epcsInitialize.iframeInstanceId}
                    data-test-id="epcs-iframe"
                    name="payment0"
                    title="payment-view"
                    scrolling="no"
                    frameBorder="0"
                    tabIndex="4"
                    width="100%"
                    height="215px"
                    src={this.props.epcsInitialize.iframeSource.replace("&amp;", "&")}
                    className="component-label i-frame"
                    onLoad={this.onLoadHandler}/>
              : <Loader size="lg"/>}
        </div>
      );
    } else {
      return (
        <CreditCardInfo enableIframe={this.enableIframe}/>
      );
    }
  }

  enableIframe = () => {
    this.setState({ iFrameLoadSuccessful: false });
    // Notify the parent that the iframe is no longer ready so the Confirm
    // button is disabled again while the user edits a previously saved card
    // and a fresh iframe is loaded/verified.
    this.notifyIframeReadyChange(false);
    this.props.refreshEpcsStatus();
  }

  render() {
    return (
      <div>
        {this.showEpcsIframe()}
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    hasValidCard: state.epcsStatus.hasSubmittedValidCard,
    epcsStatus: state.epcsStatus,
    serviceInfo: getServiceInfo(state)
  };
}

export default connect(mapStateToProps, {
  requestEpcsStatus,
  refreshEpcsStatus
})(EpcsIframe);
